import React, { useMemo, useContext } from "react";
import ReactSelect, { type Props, components } from "react-select";
import { ErrorOutlineOutlined } from "@material-ui/icons";
import cx from "classnames";

import { ThemeContextNuO } from "../../nuorder-theme";
import { DATATEST } from "./constants";
import { useStyles, selectCustomStyles } from "./select.styles";

export type TSelectedOption<Value> = {
    value: Value;
    label: string;
} | null;

type TSelectProps =
    | Props
    | {
          name: string;
          defaultValue?: string;
          label?: string;
          placeholder?: string;
          error?: string;
          options: {
              value: string;
              label: string;
          }[];

          startIcon?: React.ReactNode;

          isRequired?: boolean;
          autoFocus?: boolean;
          isLoading?: boolean;
          isDisabled?: boolean;
          isClearable?: boolean;

          className?: string;

          onChange: (option: TSelectedOption<any>) => void;
      };

export const Select = (props: TSelectProps) => {
    const themeContextNuO = useContext(ThemeContextNuO);
    const styles = useStyles(themeContextNuO);

    const customComponents = useMemo(
        () => ({
            IndicatorSeparator: () => null,
            Control: ({ children, ...props }) => {
                const StartIcon = props.selectProps?.startIcon ? props.selectProps?.startIcon : null;

                return (
                    <components.Control {...props}>
                        {StartIcon ? <StartIcon fontSize="large" /> : null}
                        {children}
                    </components.Control>
                );
            },
        }),

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.startIcon]
    );

    return (
        <div className={cx(styles.root, props.className)} data-test={DATATEST.ROOT}>
            {props.label && (
                <label className={styles.label} data-test={DATATEST.LABEL}>
                    {props.label} {Boolean(props.isRequired) && <span className={styles.asterix}>*</span>}
                </label>
            )}
            <ReactSelect
                {...props}
                name={props.name}
                defaultValue={props.defaultValue}
                placeholder={props.placeholder || ""}
                options={props.options}
                isLoading={Boolean(props.isLoading)}
                autoFocus={Boolean(props.autoFocus)}
                isClearable={Boolean(props.isClearable)}
                isDisabled={Boolean(props.isDisabled)}
                onChange={props.onChange}
                components={customComponents}
                styles={selectCustomStyles({
                    hasError: Boolean(props.error),
                    theme: themeContextNuO,
                    hasStartIcon: Boolean(props.startIcon),
                })}
            />
            {props.error && (
                <div className={styles.error} data-test={DATATEST.ERROR_MESSAGE}>
                    <ErrorOutlineOutlined className={styles.errorIcon} data-test={DATATEST.ERROR_ICON} />
                    {props.error}
                </div>
            )}
        </div>
    );
};
